/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';

const isValidCallback = (callback) => callback && typeof callback === 'function';

export const useExitIntent = (callback) => {
    const handleExitIntent = (event) => {
        if (event.clientY <= 5) {
            if (isValidCallback(callback)) {
                callback('exit');
            }
        }
    };

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.addEventListener('mouseleave', handleExitIntent);
        }

        return () => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('mouseleave', handleExitIntent);
            }
        };
    }, [callback]);
};

export const useScrollDetection = (callback, speedThreshold = 1.0) => {
    const isMobile = useSelector(getSSRDeviceType) === 'mobile';
    const previousScroll = useRef(0);
    const lastTimestamp = useRef(Date.now());

    const handleScroll = () => {
        const currentScrollY = window.scrollY || window.pageYOffset;
        const currentTimestamp = Date.now();
        const timeDiff = currentTimestamp - lastTimestamp.current;

        if (timeDiff > 0) {
            // Positive distance means scrolling UP (towards 0)
            const distance = previousScroll.current - currentScrollY;
            const velocity = distance / timeDiff; // speed in pixels per millisecond

            // velocity > 1.0 means moving faster than 1000px per second (a rapid swipe)
            if (distance > 80 && velocity > speedThreshold && currentScrollY < 300) {
                if (isValidCallback(callback)) {
                    callback('exit');
                }
            }
        }

        previousScroll.current = currentScrollY;
        lastTimestamp.current = currentTimestamp;
    };

    useEffect(() => {
        previousScroll.current = window.scrollY || window.pageYOffset;
        lastTimestamp.current = Date.now();

        if (typeof window !== 'undefined' && isMobile) {
            window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
        }

        return () => {
            if (typeof window !== 'undefined' && isMobile) {
                window.removeEventListener('scroll', handleScroll, { capture: true });
            }
        };
    }, [callback, isMobile, speedThreshold]);
};

export default { useExitIntent };
